/**
 * sceneView.ts — Merge project/scene view limits; apply mobile_view only on mobile
 */
import { I_ConfigScene, I_Scene } from '@containers/Home/types/I_Scene';
import { selectDetailProject } from '@containers/Home/selectors';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

export const sceneViewRevision = (view?: I_ConfigScene) =>
  view
    ? JSON.stringify({
        limit_view: view.limit_view,
        zoom_limit: view.zoom_limit,
        default_view: view.default_view,
        mobile_view: view.mobile_view,
      })
    : '';

export const DEFAULT_SCENE_VIEW: Required<
  Pick<I_ConfigScene, 'limit_view' | 'zoom_limit' | 'default_view'>
> = {
  limit_view: {
    top: -90,
    bottom: 90,
    left: -180,
    right: 180,
  },
  zoom_limit: {
    min: 60,
    max: 150,
  },
  default_view: {
    x: 0,
    y: 0,
    zoom_lv: 90,
  },
};

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(max-width: 768px)').matches ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  );
};

export const mergeSceneView = (
  sceneConfig?: I_ConfigScene,
  projectGlobalViewConfig?: I_ConfigScene,
): Pick<
  I_ConfigScene,
  'limit_view' | 'zoom_limit' | 'default_view' | 'mobile_view'
> => ({
  limit_view: {
    ...DEFAULT_SCENE_VIEW.limit_view,
    ...projectGlobalViewConfig?.limit_view,
    ...sceneConfig?.limit_view,
  },
  zoom_limit: {
    ...DEFAULT_SCENE_VIEW.zoom_limit,
    ...projectGlobalViewConfig?.zoom_limit,
    ...sceneConfig?.zoom_limit,
  },
  default_view: {
    ...DEFAULT_SCENE_VIEW.default_view,
    ...projectGlobalViewConfig?.default_view,
    ...sceneConfig?.default_view,
  },
  mobile_view:
    sceneConfig?.mobile_view !== undefined
      ? sceneConfig.mobile_view
      : projectGlobalViewConfig?.mobile_view,
});

export const applyMobileViewOverrides = (
  baseView: I_ConfigScene,
  isMobile = isMobileDevice(),
): I_ConfigScene => {
  if (!isMobile || !baseView.mobile_view) return baseView;
  const mobile = baseView.mobile_view;
  return {
    ...baseView,
    limit_view: { ...baseView.limit_view, ...mobile.limit_view },
    zoom_limit: { ...baseView.zoom_limit, ...mobile.zoom_limit },
    default_view: {
      ...baseView.default_view,
      ...(mobile.default_view?.zoom_lv !== undefined
        ? { zoom_lv: mobile.default_view.zoom_lv }
        : {}),
    },
  };
};

export const withSceneView = (
  scene?: I_Scene,
  projectGlobalViewConfig?: I_ConfigScene,
): I_Scene | undefined => {
  if (!scene) return scene;
  const view = mergeSceneView(scene.config, projectGlobalViewConfig);
  return {
    ...scene,
    config: {
      ...scene.config,
      default_view: view.default_view,
      limit_view: view.limit_view,
      zoom_limit: view.zoom_limit,
      mobile_view: view.mobile_view,
    },
  };
};

export const useSceneView = (scene?: I_Scene) => {
  const project = useSelector(selectDetailProject);
  const globalView = project?.config?.setting?.view;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  return useMemo(() => {
    const withView = withSceneView(scene, globalView);
    if (!withView) return withView;
    return {
      ...withView,
      config: applyMobileViewOverrides(withView.config, isMobile),
    };
  }, [scene, globalView, isMobile]);
};
